import { DATASET_INITIAL_ID, PROJECT_ID_PLACEHOLDER, PROJECT_ID_PREFIX } from 'common/constants';
import {
  AppModule,
  AppPage,
  AppProject,
  Feature,
  ProjectParameter,
  ProjectParameters,
} from 'common/models/page-config.models';
import { RootState } from 'common/store';
import { clearChartFilters } from 'common/store/slices/chart-filters.slice';
import { clearFilters } from 'common/store/slices/common-filters.slice';
import { resetDateFilters } from 'common/store/slices/date-filter.slice';
import { toggleFilters } from 'common/store/slices/filter-builder.slice';
import { clearGroupAndAggreates } from 'common/store/slices/page-composer.slice';
import {
  selectLastActiveModule,
  selectLastActiveProjectByModuleId,
  selectModules,
  setLastActiveModule,
  setLastActiveProject,
} from 'common/store/slices/page-config.slice';
import { clearWidgetSearchQuery } from 'common/store/slices/search.slice';
import { clearAllWidgetData } from 'common/store/slices/widget-data.slice';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export interface ModuleProjectContext {
  modules: AppModule[];
  activeModule: AppModule | undefined;
  activeProject: AppProject | undefined;
  projectFeatures: Feature[];
  projects: AppProject[];
  captureProjects: AppProject[];
  lastActiveModule: AppModule | undefined;
  lastActiveProject: AppProject | undefined;
  changeProject: (newProjectId: string) => void;
  getProjectParameter: (parameter: ProjectParameter) => string | string[] | undefined;
}

const ModuleProjectContext = createContext<ModuleProjectContext>({
  modules: [],
  activeModule: undefined,
  activeProject: undefined,
  projectFeatures: [],
  projects: [],
  captureProjects: [],
  lastActiveModule: undefined,
  lastActiveProject: undefined,
  changeProject: () => {},
  getProjectParameter: () => undefined,
});

const ModuleProjectProvider = (props: object) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { module, projectId } = useParams();
  const navigate = useNavigate();
  const modules = useSelector(selectModules);
  const lastActiveModuleId = useSelector(selectLastActiveModule);
  const lastActiveProjectId = useSelector((state: RootState) =>
    selectLastActiveProjectByModuleId(state, lastActiveModuleId)
  );

  const currentProjectId = useRef<string | undefined>(projectId);

  // Backup module from projectId because Files doesn't have a module
  const moduleFromProjectId = useMemo(() => {
    return modules?.find((moduleItem) =>
      moduleItem.projects.find((projectItem) => {
        return projectItem.moduleProjectID === projectId?.replace(PROJECT_ID_PREFIX, '');
      })
    );
  }, [projectId, modules]);

  const activeModule =
    useMemo(() => {
      return (
        modules?.find((moduleItem) => moduleItem.route === `/${module}`) ||
        modules?.find((moduleItem) => moduleItem.route === '/insights')
      );
    }, [modules, module]) ?? moduleFromProjectId;

  const projects = useMemo(() => {
    return activeModule?.projects ?? [];
  }, [activeModule]);

  const captureProjects = useMemo(() => {
    return (
      activeModule?.projects?.filter((project: AppProject) =>
        project.pages.find((page: AppPage) =>
          page.PageDisplayName?.toLowerCase().includes('capture')
        )
      ) ?? []
    );
  }, [activeModule]);

  const activeProject = useMemo(() => {
    if (!activeModule?.projects || !projectId) return undefined;

    const plainProjectId = projectId.replace(PROJECT_ID_PREFIX, '');
    return activeModule.projects.find((project) => project.moduleProjectID === plainProjectId);
  }, [activeModule, projectId]);

  useEffect(() => {
    if (!activeModule?.moduleID || !projectId) return;

    const plainProjectId = projectId.replace(PROJECT_ID_PREFIX, '');
    dispatch(setLastActiveModule(activeModule.moduleID));

    if (projectId !== PROJECT_ID_PLACEHOLDER) {
      dispatch(
        setLastActiveProject({
          moduleId: activeModule.moduleID,
          projectId: plainProjectId,
        })
      );

      //Clear all widget data and search query on project change
      dispatch(clearAllWidgetData());
      dispatch(clearWidgetSearchQuery());
      dispatch(clearGroupAndAggreates());
      dispatch(toggleFilters({ visible: false }));

      // Only if projectId changes, clear filters
      if (currentProjectId.current !== projectId) {
        dispatch(clearChartFilters({ datasetId: DATASET_INITIAL_ID }));
        dispatch(resetDateFilters({ datasetId: DATASET_INITIAL_ID }));
        dispatch(clearFilters({ datasetId: DATASET_INITIAL_ID }));
      }
      currentProjectId.current = projectId;
    }
  }, [activeModule?.moduleID, projectId, dispatch]);

  const projectFeatures = useMemo(() => {
    if (!activeProject) return [];

    return activeProject.parameters.reduce((features: Feature[], param: ProjectParameters) => {
      if (param.name === ProjectParameter.Feature) {
        features.push(param.value as Feature);
      }
      return features;
    }, []);
  }, [activeProject]);

  const lastActiveModule = useMemo(() => {
    if (!lastActiveModuleId) return undefined;

    return modules?.find((module) => module.moduleID === lastActiveModuleId);
  }, [lastActiveModuleId, modules]);

  const lastActiveProject = useMemo(() => {
    if (!lastActiveProjectId) return undefined;

    return lastActiveModule?.projects?.find(
      (project) => project.moduleProjectID === lastActiveProjectId
    );
  }, [lastActiveProjectId, lastActiveModule]);

  const changeProject = useCallback(
    (newProjectId: string) => {
      if (pathname.includes(PROJECT_ID_PREFIX) || pathname.includes('projectId')) {
        const path = pathname.replace(projectId as string, `${PROJECT_ID_PREFIX}${newProjectId}`);
        return navigate(`${path}${search}`, {
          replace: true,
        });
      } else {
        const path = pathname.replace(projectId as string, `${PROJECT_ID_PREFIX}${newProjectId}`);
        return navigate(`${path}${search}`, {
          replace: true,
        });
      }
    },
    [navigate, pathname, projectId, search]
  );

  const getProjectParameter = useCallback(
    (parameter: ProjectParameter): string | string[] | undefined => {
      const result = activeProject?.parameters.filter((param) => param.name === parameter);

      if (!result || result.length === 0) {
        return undefined;
      }

      if (result.length === 1) {
        return result[0].value;
      } else {
        return result?.map((param) => param.value);
      }
    },
    [activeProject?.parameters]
  );

  const value = useMemo(
    () => ({
      activeModule,
      activeProject,
      captureProjects,
      changeProject,
      lastActiveModule,
      lastActiveProject,
      modules,
      projectFeatures,
      projects,
      getProjectParameter,
    }),
    [
      activeModule,
      activeProject,
      captureProjects,
      changeProject,
      lastActiveModule,
      lastActiveProject,
      modules,
      projectFeatures,
      projects,
      getProjectParameter,
    ]
  );

  return <ModuleProjectContext.Provider value={value} {...props} />;
};

const useModuleProjectContext = () => useContext(ModuleProjectContext);

export { ModuleProjectProvider, useModuleProjectContext };
